import * as React from 'react'
import styled from 'styled-components'
import { IJsonResponse } from 'core/JsonApiSerializer'
import * as interfaces from 'static/interfaces'
import GuideAssignmentFilter from 'components/molecules/Guide/GuideAssignmentFilter'
import { GuideAssignmentStatusesLog } from 'components/organisms/Guides/GuideAssignmentStatusesLog'
import * as constants from 'static/constants'
import { agentService } from 'core/services'
import { Spinner, Panel, PanelTitle } from 'components/atoms'
import { useDebounce } from 'utils/hooks'
import moment from 'moment'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'

declare var window: interfaces.IWindow

interface IProps {
  request_statuses: Record<string, number>
  user_json: interfaces.IUser
  guide_json: IJsonResponse
}

const DEBOUNCE_DELAY = 300

const GuideAssignmentStatusesPage: React.FC<IProps> = ({
  user_json,
  request_statuses,
  guide_json,
}) => {
  const { data: guide } = agentService.getDataFromJson(guide_json)
  const { data: user } = agentService.getDataFromJson(user_json)
  const [month, setMonth] = React.useState<moment.Moment>(moment())
  const [postType, setPostType] = React.useState<string | null>(null)
  const [requestStatuses, setRequestStatuses] = React.useState<string[]>([])
  const [tourGuides, setTourGuides] = React.useState<interfaces.ITourGuide[]>([])
  const [loading, setLoading] = React.useState(false)

  const debouncedMonth = useDebounce<moment.Moment>(month, DEBOUNCE_DELAY)
  const debouncedPostType = useDebounce<string | null>(postType, DEBOUNCE_DELAY)
  const debouncedRequestStatuses = useDebounce<string[]>(requestStatuses, DEBOUNCE_DELAY)

  // ツアーアサイン状況を取得
  React.useEffect(() => {
    if (!guide?.id || !month) {
      return
    }

    const fetchData = async () => {
      setLoading(true)
      try {
        const { tourGuides: fetchedTourGuides } = await agentService.getTourGuidesByAssignments({
          guide_id: guide.id,
          month: debouncedMonth.format('YYYY-MM'),
          post_type: debouncedPostType,
          request_statuses: debouncedRequestStatuses,
        })
        setTourGuides(fetchedTourGuides)
      } catch (error) {
        window.flashMessages.addMessage({
          text: `ガイドアサイン状況の取得に失敗しました: ${error.message}`,
          type: 'error',
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [guide?.id, debouncedMonth, debouncedPostType, debouncedRequestStatuses])

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <Panel>
          <PanelTitle title={'ガイドアサイン状況'} />
          {guide && (
            <GuideInfo>
              <a className="guideName" href={`/agent/guides/${guide.id}/edit`}>
                {guide.fullname}
              </a>
            </GuideInfo>
          )}
          <FilterSection>
            <GuideAssignmentFilter
              selectedMonth={month}
              onMonthChange={setMonth}
              selectedPostType={postType}
              onPostTypeChange={setPostType}
              selectedRequestStatuses={requestStatuses}
              onRequestStatusesChange={setRequestStatuses}
              allRequestStatuses={request_statuses}
            />
          </FilterSection>

          {guide && month && (
            <StatsSection>
              {loading ? <Spinner /> : <GuideAssignmentStatusesLog tourGuides={tourGuides} />}
            </StatsSection>
          )}
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const Wrapper = styled.div`
  min-height: 720px;
`

const GuideInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;

  .guideName {
    font-size: 20px;
    font-weight: bold;
    color: ${constants.COLORS.primary};
  }
`

const FilterSection = styled.div`
  padding-bottom: 20px;
  border-bottom: 1px solid ${constants.COLORS.divider2};
`

const StatsSection = styled.div`
  margin: 20px 0;
  min-height: 240px;
`

export default GuideAssignmentStatusesPage
